import { Button } from "@ondit/ui";
import { PanelRight } from "@ondit/ui/icons";
import { createContext, useContext } from "react";
import { useT } from "~/core/i18n";

/**
 * The column on the right of the pane, where the Agent answers questions
 * about the screen beside it.
 *
 * The shell's rather than a module's: Analytics owns the button and the Agent
 * owns what is in the column, and neither may import the other. The router,
 * which already imports both, puts them together.
 */
export interface Aside {
	/** Whether this account and this screen can have the column at all. */
	available: boolean;
	open: boolean;
	setOpen: (open: boolean) => void;
}

export const AsideContext = createContext<Aside>({ available: false, open: false, setOpen: () => {} });

export function useAside(): Aside {
	return useContext(AsideContext);
}

/** The head-of-page control that opens and closes the column. */
export function AsideToggle() {
	const t = useT();
	const { available, open, setOpen } = useAside();
	// Open, the column carries its own fold at its edge; two would be one too many.
	if (!available || open) return null;
	return (
		<Button
			variant="ghost"
			size="icon"
			type="button"
			className="aside-toggle"
			aria-pressed={open}
			aria-label={t("aside.ask")}
			title={t("aside.ask")}
			onClick={() => setOpen(!open)}
		>
			<PanelRight aria-hidden />
		</Button>
	);
}
